import type { Schemas } from "#shopware";
import { ref, watch } from "vue";

const _useProductSetConfiguration = () => {
  const crossSellings = ref<Schemas["CrossSellingElement"][]>([]);

  const productConfigurationSet = computed(() => {
    return crossSellings.value?.find(
      (crossSellingItem: Schemas["CrossSellingElement"]) =>
        crossSellingItem.crossSelling?.display &&
        crossSellingItem.crossSelling?.display !== "sliderOnly"
    );
  });

  const setItems = computed(() => {
    return productConfigurationSet.value?.products || [];
  });

  const assignedProductsData = computed(() => {
    return productConfigurationSet.value?.crossSelling?.assignedProducts || [];
  });

  const getAssignedProductData = (productId: string) => {
    return assignedProductsData.value.find(
      (item: any) => item.productId === productId
    );
  };

  const itemsToCart = ref<{ productId: string }[]>([]);

  const isItemInCart = (productId: string) => {
    return itemsToCart.value.some((item) => item.productId === productId);
  };

  const removeItemFromCart = (productId: string) => {
    itemsToCart.value = itemsToCart.value.filter(
      (item) => item.productId !== productId
    );
  };

  const assignItemToCart = (productId: string) => {
    itemsToCart.value.push({ productId });
  };

  const setAllItemsToCart = () => {
    itemsToCart.value = Array.from(
      new Set(setItems.value.map((item) => item.id))
    ).map((productId) => ({ productId }));
  };

  const updateItemInSet = (
    productId: string,
    newProductData: Partial<Schemas["Product"]>
  ) => {
    const index = setItems.value.findIndex(
      (item: Schemas["Product"]) => item.id === productId
    );
    if (index !== -1) {
      setItems.value[index] = {
        ...setItems.value[index],
        ...newProductData,
      };

      // Sync the itemsToCart value
      const existingItemInCart = itemsToCart.value.find(
        (item) => item.productId === productId
      );
      if (existingItemInCart) {
        existingItemInCart.productId = newProductData.id as string;
      }

      const assignedProductDataIndex = assignedProductsData.value.findIndex(
        (item) => item.productId === productId
      );
      if (assignedProductDataIndex !== -1) {
        assignedProductsData.value[assignedProductDataIndex].productId =
          newProductData.id as string;
      }
    }
  };

  const setCrossSellings = (
    _crossSellings: Schemas["CrossSellingElement"][]
  ) => {
    crossSellings.value = _crossSellings;
  };

  watch(setItems, () => {
    setAllItemsToCart();
  });

  // Reset data on route change
  const route = useRoute();
  watch(
    () => route.path,
    () => {
      crossSellings.value = [];
      itemsToCart.value = [];
    }
  );

  return {
    crossSellings,
    setCrossSellings,
    itemsToCart,
    assignItemToCart,
    removeItemFromCart,
    setAllItemsToCart,
    productConfigurationSet,
    setItems,
    getAssignedProductData,
    updateItemInSet,
    isItemInCart,
  };
};

export const useProductSetConfiguration = createSharedComposable(
  _useProductSetConfiguration
);
